/**
 * @file Switches between the create and activate steps of mobile verification.
 */
import React, { useState } from "react";

import { enableMyAccount2FAOperation as operation } from "@api/services/investments";
import type { Operation } from "@components/AppContext/types";

import CreateVerificationRequest from "./CreateVerificationRequest";
import ActivateVerificationRequest from "./ActivateVerificationRequest";

type Props = {
  enableOperation?: Operation;
  mobileNumber: string;
  onSuccess: () => Promise<void> | void;
}

/**
 * Renders the create step, the activate step, or nothing, based on the
 * mobile number and the pending verification request.
 */
const MobileVerificationRequest = ({
  enableOperation = operation,
  mobileNumber,
  onSuccess,
}: Props) => {
  const [ verificationRequestId, setVerificationRequestId ] = useState<string>();

  /**
   * Clears the pending verification request so a new code can be sent.
   */
  const onResendVerificationCode = () => setVerificationRequestId(undefined);

  const hasVerificationRequestId = Boolean(verificationRequestId);
  if (hasVerificationRequestId) {
    const activateProps = {
      mobileNumber,
      onSuccess,
      enableOperation,
      verificationRequestId,
      onResendVerificationCode,
    };

    return (
      <ActivateVerificationRequest {...activateProps} />
    );
  }

  const hasMobileNumber = Boolean(mobileNumber);
  if (hasMobileNumber) {
    const createProps = {
      mobileNumber,
      enableOperation,
      setVerificationRequestId,
    };

    return (
      <CreateVerificationRequest {...createProps} />
    );
  }

  return null;
};

export default MobileVerificationRequest;
